import { useEffect, useRef, useState } from 'react';
import { useApp } from '@contexts/AppContext';
import { detectEntityFromIp } from '@services/geoLocation';

const ENTITY_CACHE_KEY = 'mrt-entity-code';

function readEntityCache() {
  try {
    return localStorage.getItem(ENTITY_CACHE_KEY) || null;
  } catch {
    return null;
  }
}

function writeEntityCache(code) {
  try {
    if (code) localStorage.setItem(ENTITY_CACHE_KEY, code);
  } catch {
    // ignore — private mode / quota
  }
}

/**
 * useRegion — sales area / sales entity / customer-account state for MRT.
 *
 * Contract (preserved from earlier scaffolding):
 *   - salesArea, salesEntity, customerAccount      (current state)
 *   - setSalesArea, setSalesEntity, setCustomerAccount  (user-initiated setters)
 *
 * Plus new (Sub-Plan 4 bonus):
 *   - geoResolvedEntity — `true` when the current salesEntity was filled in by
 *     IP-based detection rather than a user pick or a cached previous pick.
 *     Lets consumers (e.g. AccountSwitcher) hint "auto-detected, please
 *     confirm".
 *   - resolving — `true` while the IP lookup is in flight on first mount.
 *
 * Detection logic (runs once per page session):
 *   1. If state.salesEntity already populated by a logged-in user's
 *      AccountSwitcher pick → do nothing.
 *   2. Else if localStorage `mrt-entity-code` has a cached previous pick →
 *      hydrate state with it (treated as user-picked, not geo).
 *   3. Else call detectEntityFromIp() → on success, dispatch SET_SALES_ENTITY
 *      with the resolved code AND set geoResolvedEntity=true. Falls back to
 *      'SE' on any error (handled inside geoLocation).
 *
 * User picks always win — once setSalesEntity is called, the cached value is
 * updated and geoResolvedEntity flips to false.
 */
export default function useRegion() {
  const { state, dispatch } = useApp();
  const [geoResolvedEntity, setGeoResolvedEntity] = useState(false);
  const [resolving, setResolving] = useState(false);
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    if (state.salesEntity) {
      // Already populated (e.g. by an in-flight session, or AccountSwitcher
      // mounted earlier). Don't overwrite.
      return;
    }

    const cached = readEntityCache();
    if (cached) {
      dispatch({ type: 'SET_SALES_ENTITY', payload: cached });
      return;
    }

    let cancelled = false;
    setResolving(true);
    detectEntityFromIp()
      .then((code) => {
        if (cancelled) return;
        if (code) {
          dispatch({ type: 'SET_SALES_ENTITY', payload: code });
          writeEntityCache(code);
          setGeoResolvedEntity(true);
        }
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSalesArea = (area) => {
    dispatch({ type: 'SET_SALES_AREA', payload: area });
    dispatch({ type: 'SET_SALES_ENTITY', payload: null }); // reset cascade
    dispatch({ type: 'SET_CUSTOMER_ACCOUNT', payload: null });
  };

  const setSalesEntity = (entity) => {
    dispatch({ type: 'SET_SALES_ENTITY', payload: entity });
    dispatch({ type: 'SET_CUSTOMER_ACCOUNT', payload: null });
    writeEntityCache(entity);
    setGeoResolvedEntity(false);
  };

  const setCustomerAccount = (account) => {
    dispatch({ type: 'SET_CUSTOMER_ACCOUNT', payload: account });
  };

  return {
    salesArea: state.salesArea,
    salesEntity: state.salesEntity,
    customerAccount: state.customerAccount,
    geoResolvedEntity,
    resolving,
    setSalesArea,
    setSalesEntity,
    setCustomerAccount,
  };
}
